import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Subscription, Observable } from 'rxjs';
import { Conversation, TeamInfo } from '../../../../core/models/conversation.model';
import { AuthSignalService } from '../../../../core/state/auth.signal';
import { SocketService } from '../../../../core/socket/socket.service';
import { UnreadService } from '../../../../core/services/unread.service';
import { environment } from '../../../../../environments/environment';

export type TabType = 'global' | 'mine' | 'team' | 'all';

@Injectable({
  providedIn: 'root'
})
export class InboxService implements OnDestroy {
  private apiUrl = environment.apiUrl;
  private subs = new Subscription();

  // State
  private _globalConvs = new BehaviorSubject<Conversation[]>([]);
  private _mineConvs = new BehaviorSubject<Conversation[]>([]);
  private _teamConvs = new BehaviorSubject<Conversation[]>([]);
  private _allConvs = new BehaviorSubject<Conversation[]>([]);
  private activeNotifications = new Map<number, Notification>();

  public globalConvs$ = this._globalConvs.asObservable();
  public mineConvs$ = this._mineConvs.asObservable();
  public teamConvs$ = this._teamConvs.asObservable();
  public allConvs$ = this._allConvs.asObservable();

  private _myTeams = new BehaviorSubject<TeamInfo[]>([]);
  public myTeams$ = this._myTeams.asObservable();

  private _allTags = new BehaviorSubject<any[]>([]);
  public allTags$ = this._allTags.asObservable();

  private _phoneAccounts = new BehaviorSubject<any[]>([]);
  public phoneAccounts$ = this._phoneAccounts.asObservable();

  // Selected phone account filter (null = show all, used by admin/superadmin)
  public selectedAccountId: number | null = null;

  private _loading = new BehaviorSubject<boolean>(false);
  public loading$ = this._loading.asObservable();
  
  public searchQuery = '';

  // Filtro "Con el bot": trae solo conversaciones con interacción del bot (server-side).
  public botOnly = false;

  // Cursors for load-more (last conversation id per tab)
  private _cursors: { global: number | null; mine: number | null; team: number | null; all: number | null } =
    { global: null, mine: null, team: null, all: null };

  private _hasMoreGlobal = new BehaviorSubject<boolean>(false);
  private _hasMoreMine = new BehaviorSubject<boolean>(false);
  private _hasMoreTeam = new BehaviorSubject<boolean>(false);
  private _hasMoreAll = new BehaviorSubject<boolean>(false);
  public hasMoreGlobal$ = this._hasMoreGlobal.asObservable();
  public hasMoreMine$ = this._hasMoreMine.asObservable();
  public hasMoreTeam$ = this._hasMoreTeam.asObservable();
  public hasMoreAll$ = this._hasMoreAll.asObservable();

  private readonly PAGE_LIMIT = 150;


  public currentConvId: number | null = null;
  private initialized = false;
  private lastReloadAt = 0;

  constructor(
    private http: HttpClient,
    public authSignal: AuthSignalService,
    private socketService: SocketService,
    public unread: UnreadService
  ) {}

  get isAdmin() { return ['ADMIN', 'SUPERADMIN'].includes(this.authSignal.user()?.role ?? ''); }
  get isSuperAdmin() { return this.authSignal.user()?.role === 'SUPERADMIN'; }
  get myTeamIds() { return this._myTeams.value.map(t => t.teamId); }

  /**
   * ¿Ve TODAS las líneas / la pestaña "Todos"? Por enum (ADMIN/SUPERADMIN) o por permiso RBAC
   * `chats.tab_all` (roles Administrador/Supervisor con enum AGENT). No depender solo del enum
   * evita que un admin RBAC no vea/actualice la pestaña "Todos".
   */
  get canSeeAllLines() { return this.isAdmin || this.authSignal.has('chats.tab_all'); }

  /**
   * IDs de las cuentas de teléfono (WhatsApp) accesibles para el agente actual.
   * Se deriva de los equipos a los que pertenece.
   * Quien puede ver todas las líneas no tiene restricción (devuelve null).
   */
  get accessibleAccountIds(): number[] | null {
    if (this.canSeeAllLines) return null; // null = sin restricción
    return this._myTeams.value
      .map(t => t.team.phoneAccountId)
      .filter((id): id is number => id !== null && id !== undefined);
  }

  /**
   * Determina si el agente actual puede ver una conversación dada.
   * Reglas:
   *   - Admins/Superadmins: ven todo.
   *   - Agente con equipos: solo ve conversaciones de cuentas vinculadas a sus equipos.
   *   - Agente sin equipos: no ve nada (accessibleAccountIds === []).
   *   - Conversación sin phoneAccount: visible solo para admins (caso legacy).
   */
  canSeeConversation(data: any): boolean {
    const accessible = this.accessibleAccountIds;
    if (accessible === null) return true; // admin
    if (accessible.length === 0) return false; // sin equipos → no ve nada

    const convPhoneAccountId: number | null =
      data.phoneAccount?.id ?? data.phoneAccountId ?? null;

    if (convPhoneAccountId === null) return false; // sin account asignado → solo admins
    return accessible.includes(convPhoneAccountId);
  }

  public init() {
    // Guard: avoid duplicating socket subscriptions if called more than once
    if (this.initialized) return;
    this.initialized = true;

    // Connect the socket (was missing — this is why sockets weren't working)
    this.socketService.connect(this.authSignal.user()?.userId);

    this.http.get<any[]>(`${this.apiUrl}/tags`).subscribe(t => this._allTags.next(t));

    // Cargar la lista de líneas para quien puede ver todas (enum admin o permiso chats.tab_all)
    if (this.canSeeAllLines) {
      this.http.get<any[]>(`${this.apiUrl}/phone-accounts`).subscribe({
        next: accounts => this._phoneAccounts.next(accounts),
        error: () => {}
      });
    }

    this.http.get<TeamInfo[]>(`${this.apiUrl}/users/me/teams`).subscribe({
      next: teams => {
        this._myTeams.next(teams);
        this.loadAllTabs();
      },
      error: () => this.loadAllTabs()
    });

    this.setupSockets();
    this.setupVisibilityListener();
    this.requestNotificationPermission();
  }

  public reload() {
    const now = Date.now();
    // Debounce: do not reload more than once every 30 seconds
    if (now - this.lastReloadAt < 30_000) return;
    this.lastReloadAt = now;
    this.http.get<any[]>(`${this.apiUrl}/tags`).subscribe(t => this._allTags.next(t));
    this.loadAllTabs();
  }

  private requestNotificationPermission() {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }

  private setupVisibilityListener() {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        console.log('[InboxService] App became visible. Re-syncing background data...');
        this.socketService.connect(this.authSignal.user()?.userId);
        this.reload();

        // Si regresamos al tab y tenemos un chat abierto, lo marcamos como leído ahora
        if (this.currentConvId) {
          this.unread.markRead(this.currentConvId);
        }
      }
    });
  }

  private setupSockets() {
    this.subs.add(
      this.socketService.onNewConversation().subscribe(conv => {
        if (!this.canSeeConversation(conv)) return;
        this.upsertConv(conv, 'all');
        if (conv.id !== this.currentConvId) {
            // MEJORA: Usar el conteo absoluto del backend para evitar conteos dobles en reconexiones/re-aperturas
            this.unread.applySocketRead({ 
                conversationId: conv.id, 
                unreadCount: conv.unreadCount ?? 1 
            });
            this.evaluateAndShowNotification(conv, true);
        }
      })
    );

    this.subs.add(
      this.socketService.onConversationClaimed().subscribe((update: any) => {
        if (!this.canSeeConversation(update)) return;

        const hasNewMsg = !!update.lastMessage;
        
        // Detectar si hubo cambio de propietario (Rule 3)
        const oldConv = 
          this._mineConvs.value.find(c => c.id === update.id) || 
          this._globalConvs.value.find(c => c.id === update.id) ||
          this._teamConvs.value.find(c => c.id === update.id) ||
          this._allConvs.value.find(c => c.id === update.id);
          
        const myId = this.authSignal.user()?.userId;
        
        if (oldConv && oldConv.assigneeId !== update.assigneeId) {
            const contactName = update.contact?.name || `+${update.contact?.phone}`;
            if (oldConv.assigneeId === myId) {
                this.playBloop();
                this.showSystemNotification('Se te desasignó un Chat', `El chat con ${contactName} ya no te pertenece.`, update.id);
            } else if (update.assigneeId === myId) {
                this.playBloop();
                this.showSystemNotification('Nuevo Chat Asignado', `Se te asignó el chat con ${contactName}.`, update.id);
            }
        }

        if (update._zohoSync) {
          this.upsertConvInPlace(update);
          // Si fue asigado por zoho y hay nuevo mensaje (ej: webhook entró), evaluamos notificacion
          if (hasNewMsg) this.evaluateAndShowNotification(update, false);
          
          // MEJORA: Sincronizar conteo de no leídos desde Zoho Sync
          this.unread.applySocketRead({ 
              conversationId: update.id, 
              unreadCount: update.unreadCount ?? 0 
          });
          return;
        }

        this.upsertConv(update, 'update');

        const amISender = update.lastSenderId === myId;
        const isCurrentOpen = update.id === this.currentConvId;
        const isVisible = document.visibilityState === 'visible';

        if (hasNewMsg && !amISender) {
          if (isCurrentOpen && isVisible) {
            // Solo marcar como leido si realmente hay algo pendiente (evita bucles)
            if (this.unread.getCount(update.id) > 0) {
                this.unread.markRead(update.id);
            }
          } else {
            // MEJORA: Usar el conteo absoluto del backend para evitar el bug de "doble incremento" en re-aperturas
            this.unread.applySocketRead({ 
                conversationId: update.id, 
                unreadCount: update.unreadCount ?? 1 
            });
            this.evaluateAndShowNotification(update, false);
          }
        } else if (!amISender) {
            // Incluso si no hay mensaje "nuevo" (ej: sync de nombre), actualizamos el conteo por seguridad
            this.unread.applySocketRead({ 
                conversationId: update.id, 
                unreadCount: update.unreadCount ?? 0 
            });
        }
      })
    );

    this.subs.add(
      this.socketService.onConversationRead().subscribe(payload => {
        this.unread.applySocketRead(payload);
        // MEJORA: Cerrar notificación push si se leyó el chat
        if (payload.unreadCount === 0) {
            this.clearSystemNotification(payload.conversationId);
        }
      })
    );
  }

  private evaluateAndShowNotification(conv: any, isNewConversation: boolean) {
    const myId = this.authSignal.user()?.userId;
    const myTeams = this.myTeamIds;
    let shouldNotify = false;

    // Rule 1 & 2 logic
    if (conv.assigneeId) {
        // Solo notifica al propietario
        if (conv.assigneeId === myId) shouldNotify = true;
    } else {
        // No tiene propietario. Notifica al equipo
        if (conv.teamId && myTeams.includes(conv.teamId)) {
            shouldNotify = true;
        }
        // Si no tiene propietario ni team, no notifica a nadie visiblemente.
    }

    if (!shouldNotify) return;

    // Ejecuta sonido y push
    if (this.currentConvId !== conv.id || document.visibilityState !== 'visible') {
        this.playBloop();
        const contactName = conv.contact?.name || `+${conv.contact?.phone}`;
        const title = isNewConversation ? `Nueva conversación: ${contactName}` : `Mensaje de ${contactName}`;
        this.showSystemNotification(title, conv.lastMessage || 'Tienes un nuevo mensaje', conv.id);
    }
  }

  private showSystemNotification(title: string, body: string, convId?: number) {
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        // MEJORA: Evitar duplicados (throttling) — "con el primero basta"
        if (convId && this.activeNotifications.has(convId)) {
            return;
        }

        const notification = new Notification(title, {
          body,
          icon: '/favicon.ico',
          tag: convId ? `chat-${convId}` : 'system',
          // renotify: false // por defecto es false, no vibra si ya está el tag
        });

        if (convId) {
            this.activeNotifications.set(convId, notification);
            // Si el usuario la cierra manualmente, la quitamos del mapa para permitir futuras
            notification.onclose = () => this.activeNotifications.delete(convId);
        }

        notification.onclick = () => {
          window.focus();
          if (convId) window.location.href = `/dashboard/inbox/${convId}`;
          notification.close();
        };
      } catch (e) {
        console.error('[Notifications] Failed:', e);
      }
    }
  }

  private clearSystemNotification(convId: number) {
    const n = this.activeNotifications.get(convId);
    if (n) {
        n.close();
        this.activeNotifications.delete(convId);
    }
  }

  private playBloop() {
    try {
      const actx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const osc = actx.createOscillator();
      const gain = actx.createGain();
      osc.connect(gain);
      gain.connect(actx.destination);
      
      // WhatsApp style Bloop
      osc.type = 'sine';
      osc.frequency.setValueAtTime(800, actx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(1200, actx.currentTime + 0.1);
      
      gain.gain.setValueAtTime(0, actx.currentTime);
      gain.gain.linearRampToValueAtTime(0.3, actx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.01, actx.currentTime + 0.15);
      
      osc.start(actx.currentTime);
      osc.stop(actx.currentTime + 0.15);
    } catch(e) {}
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  public loadAllTabs() {
    let queryParams = this.selectedAccountId ? `?phoneAccountId=${this.selectedAccountId}` : '';
    if (this.searchQuery) {
      queryParams += (queryParams ? '&' : '?') + `search=${encodeURIComponent(this.searchQuery)}`;
    }
    if (this.botOnly) {
      queryParams += (queryParams ? '&' : '?') + 'botOnly=true';
    }

    this._cursors = { global: null, mine: null, team: null, all: null };

    this.http.get<Conversation[]>(`${this.apiUrl}/conversations/global${queryParams}`).subscribe(d => {
      this._globalConvs.next(d);
      this._cursors.global = d.length >= this.PAGE_LIMIT ? d[d.length - 1].id : null;
      this._hasMoreGlobal.next(d.length >= this.PAGE_LIMIT);
    });
    this.http.get<Conversation[]>(`${this.apiUrl}/conversations/my${queryParams}`).subscribe(d => {
      this._mineConvs.next(d);
      this._cursors.mine = d.length >= this.PAGE_LIMIT ? d[d.length - 1].id : null;
      this._hasMoreMine.next(d.length >= this.PAGE_LIMIT);
    });

    if (this.myTeamIds.length > 0) {
      this.http.post<Conversation[]>(`${this.apiUrl}/conversations/team`, { 
          teamIds: this.myTeamIds, 
          phoneAccountId: this.selectedAccountId ?? undefined,
          search: this.searchQuery || undefined,
          botOnly: this.botOnly || undefined
      }).subscribe(d => {
          this._teamConvs.next(d);
          this._cursors.team = d.length >= this.PAGE_LIMIT ? d[d.length - 1].id : null;
          this._hasMoreTeam.next(d.length >= this.PAGE_LIMIT);
      });
    }
    if (this.canSeeAllLines) {
      this.http.get<Conversation[]>(`${this.apiUrl}/conversations/all${queryParams}`).subscribe(d => {
        this._allConvs.next(d);
        this._cursors.all = d.length >= this.PAGE_LIMIT ? d[d.length - 1].id : null;
        this._hasMoreAll.next(d.length >= this.PAGE_LIMIT);
      });
    }
  }

  public loadMoreConversations(tab: TabType) {
    let queryParams = this.selectedAccountId ? `&phoneAccountId=${this.selectedAccountId}` : '';
    if (this.searchQuery) {
      queryParams += `&search=${encodeURIComponent(this.searchQuery)}`;
    }
    if (this.botOnly) {
      queryParams += '&botOnly=true';
    }

    if (tab === 'global') {
      const cursor = this._cursors.global;
      if (!cursor) return;
      this.http.get<Conversation[]>(`${this.apiUrl}/conversations/global?cursor=${cursor}${queryParams}`).subscribe(d => {
        const merged = [...this._globalConvs.value, ...d.filter(n => !this._globalConvs.value.find(e => e.id === n.id))];
        this._globalConvs.next(merged);
        this._cursors.global = d.length >= this.PAGE_LIMIT ? d[d.length - 1].id : null;
        this._hasMoreGlobal.next(d.length >= this.PAGE_LIMIT);
      });
    } else if (tab === 'mine') {
      const cursor = this._cursors.mine;
      if (!cursor) return;
      this.http.get<Conversation[]>(`${this.apiUrl}/conversations/my?cursor=${cursor}${queryParams}`).subscribe(d => {
        const merged = [...this._mineConvs.value, ...d.filter(n => !this._mineConvs.value.find(e => e.id === n.id))];
        this._mineConvs.next(merged);
        this._cursors.mine = d.length >= this.PAGE_LIMIT ? d[d.length - 1].id : null;
        this._hasMoreMine.next(d.length >= this.PAGE_LIMIT);
      });
    } else if (tab === 'team' && this.myTeamIds.length > 0) {
      const cursor = this._cursors.team;
      if (!cursor) return;
      this.http.post<Conversation[]>(`${this.apiUrl}/conversations/team`, { 
          teamIds: this.myTeamIds, 
          phoneAccountId: this.selectedAccountId ?? undefined, 
          cursor,
          search: this.searchQuery || undefined,
          botOnly: this.botOnly || undefined
      }).subscribe(d => {
        const merged = [...this._teamConvs.value, ...d.filter(n => !this._teamConvs.value.find(e => e.id === n.id))];
        this._teamConvs.next(merged);
        this._cursors.team = d.length >= this.PAGE_LIMIT ? d[d.length - 1].id : null;
        this._hasMoreTeam.next(d.length >= this.PAGE_LIMIT);
      });
    } else if (tab === 'all') {
      const cursor = this._cursors.all;
      if (!cursor) return;
      this.http.get<Conversation[]>(`${this.apiUrl}/conversations/all?cursor=${cursor}${queryParams}`).subscribe(d => {
        const merged = [...this._allConvs.value, ...d.filter(n => !this._allConvs.value.find(e => e.id === n.id))];
        this._allConvs.next(merged);
        this._cursors.all = d.length >= this.PAGE_LIMIT ? d[d.length - 1].id : null;
        this._hasMoreAll.next(d.length >= this.PAGE_LIMIT);
      });
    }
  }


  public loadConversations(tab: TabType) {
    this._loading.next(true);

    let queryParams = this.selectedAccountId ? `?phoneAccountId=${this.selectedAccountId}` : '';
    if (this.searchQuery) {
      queryParams += (queryParams ? '&' : '?') + `search=${encodeURIComponent(this.searchQuery)}`;
    }
    if (this.botOnly) {
      queryParams += (queryParams ? '&' : '?') + 'botOnly=true';
    }

    if (tab === 'global') {
      this.http.get<Conversation[]>(`${this.apiUrl}/conversations/global${queryParams}`).subscribe({
        next: d => { this._globalConvs.next(d); this._loading.next(false); },
        error: () => this._loading.next(false)
      });
      return;
    }

    const url = tab === 'mine' ? `/conversations/my${queryParams}` : tab === 'all' ? `/conversations/all${queryParams}` : null;

    if (tab === 'team' && this.myTeamIds.length) {
      this.http.post<Conversation[]>(`${this.apiUrl}/conversations/team`, { 
          teamIds: this.myTeamIds,
          phoneAccountId: this.selectedAccountId ?? undefined,
          search: this.searchQuery || undefined,
          botOnly: this.botOnly || undefined
      }).subscribe({
        next: d => { this._teamConvs.next(d); this._loading.next(false); },
        error: () => this._loading.next(false)
      });
    } else if (url) {
      this.http.get<Conversation[]>(`${this.apiUrl}${url}`).subscribe({
        next: d => {
          if (tab === 'mine') this._mineConvs.next(d);
          else if (tab === 'all') this._allConvs.next(d);
          this._loading.next(false);
        },
        error: () => this._loading.next(false)
      });
    } else {
      this._loading.next(false);
    }
  }

  public toggleTag(chatId: number, tagId: number, hasTag: boolean): Observable<any> {
    if (hasTag) {
      return this.http.delete<any>(`${this.apiUrl}/conversations/${chatId}/tags/${tagId}`);
    } else {
      return this.http.patch<any>(`${this.apiUrl}/conversations/${chatId}/tags/${tagId}`, {});
    }
  }

  public fetchTags() {
    this.http.get<any[]>(`${this.apiUrl}/tags`).subscribe(t => this._allTags.next(t));
  }

  public createTag(name: string, color: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/tags`, { name, color });
  }

  public deleteTag(id: number): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/tags/${id}`);
  }

  public upsertConv(data: any, mode: 'all' | 'update') {
    const me = this.authSignal.user()?.userId;
    const myTeams = this.myTeamIds;

    const status = data.status ?? '';
    const assigneeId: number | null = data.assignee?.id ?? data.assigneeId ?? null;
    const teamId: number | null = data.team?.id ?? data.teamId ?? null;

    const belongsToUnassigned = assigneeId === null && teamId === null;
    const belongsToMine = assigneeId === me;
    const belongsToTeam = teamId !== null && myTeams.includes(teamId);

    const belongsToGlobal = belongsToUnassigned || (status !== 'RESOLVED' && (belongsToMine || belongsToTeam));
    const belongsToAll = this.canSeeAllLines;

    // Si hay una LÍNEA seleccionada, un chat de OTRA línea no entra a ninguna lista en vivo
    // (antes se colaban los que llegaban por webhook hasta que se refrescaba manualmente).
    const convPhoneAccountId: number | null = data.phoneAccount?.id ?? data.phoneAccountId ?? null;
    const matchesLine = !this.selectedAccountId || convPhoneAccountId === this.selectedAccountId;

    const updateList = (list: Conversation[], belongs: boolean): Conversation[] => {
      const idx = list.findIndex(c => c.id === data.id);
      if (idx !== -1) {
        if (belongs) {
          list[idx] = { ...list[idx], ...data };
          list.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        } else {
          list.splice(idx, 1);
        }
      } else if (belongs && (data.contact || mode === 'all')) {
        list.unshift(data);
        list.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      }
      return list;
    };

    this._globalConvs.next(updateList([...this._globalConvs.value], matchesLine && belongsToGlobal));
    this._mineConvs.next(updateList([...this._mineConvs.value], matchesLine && belongsToMine));
    this._teamConvs.next(updateList([...this._teamConvs.value], matchesLine && belongsToTeam));
    this._allConvs.next(updateList([...this._allConvs.value], matchesLine && belongsToAll));
  }

  /**
   * Actualiza una conversación SOLO en las listas donde ya existe (sin evaluación de pertenencia).
   * Usado para eventos de fondo (Zoho sync) para no remover chats de la vista del agente.
   * El agente verá el cambio de tab/asignado en el próximo reload.
   */
  public upsertConvInPlace(data: any) {
    const updateInPlace = (list: Conversation[]): Conversation[] => {
      const idx = list.findIndex(c => c.id === data.id);
      if (idx !== -1) {
        // Preserve unreadCount from current state to avoid badge disappearing
        const currentUnread = (list[idx] as any).unreadCount;
        list[idx] = { ...list[idx], ...data, unreadCount: currentUnread ?? (data as any).unreadCount };
      }
      return list;
    };

    this._globalConvs.next(updateInPlace([...this._globalConvs.value]));
    this._mineConvs.next(updateInPlace([...this._mineConvs.value]));
    this._teamConvs.next(updateInPlace([...this._teamConvs.value]));
    this._allConvs.next(updateInPlace([...this._allConvs.value]));
  }
}
